import { Component, Suspense } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { BraxCriticalError, BraxLoading, BraxModals, BraxModalsType, BraxProviders } from 'design-system';

import { languageMapper } from './languages';
import { IntlContext, IntlContextValue } from './utils/i18n';
import handleError, { ErrorInfo } from './utils/errors';
import { withContexts } from './utils/with-context';
import authService from './services/auth';
import User from './view-model/users/User';
import UserContext from './contexts/User';
import routers from './routers';
import Notices from './components/Notices';
import AppBar from './components/AppBar';

export type AppProps = RouteComponentProps & {
    modals: BraxModalsType,
    intl: IntlContextValue
};

type AppState = {
    loading: boolean,
    user: User | null,
    error: ErrorInfo | null
};

export default withRouter(withContexts({
    modals: BraxModals,
    intl: IntlContext
}, class App extends Component<AppProps, AppState> {

    state: AppState = {
        loading: true,
        user: null,
        error: null
    };

    unsubscribe?: () => void;

    componentDidMount() {
        this.loadUser();

        this.unsubscribe = authService.subscribe(user => this.loadUser(user));
    }

    componentWillUnmount() {
        this.unsubscribe?.();
    }

    async loadUser(user?: Partial<User>) {
        if (user) {
            this.setState(prevState => prevState.user ? { user: { ...prevState.user, ...user } } : null);

            this.checkLanguage(user.language);
        } else {
            try {
                const user = (await authService.getActiveUser())?.result || null;

                this.checkLanguage(user?.language);

                this.setState({ user, loading: false });
            } catch (error) {
                this.setState({
                    error: { ...handleError(error), description: 'Você foi desconectado!' },
                    loading: false
                });
            }
        }
    }

    checkLanguage(language?: User['language']) {
        const code = language ? languageMapper[language] : null

        if (code && this.props.intl.current !== code)
            this.props.intl.change(code);
    }

    closeError() {
        this.setState({ error: null });

        this.props.history.push('/login');
    }

    render() {
        return (<>
            { this.state.loading ? (
                <BraxProviders>
                    <BraxLoading />
                </BraxProviders>
            ) : this.state.error ? (
                <BraxProviders>
                    <BraxCriticalError {...this.state.error} onClose={() => this.closeError()} />
                </BraxProviders>
            ) : this.state.user ? (
                <UserContext.Provider value={this.state.user}>
                    <BraxProviders>
                        <AppBar>
                            <Notices />

                            <Suspense fallback={<BraxLoading overlay={false} />}>
                                { routers.rendered.authenticated }
                            </Suspense>
                        </AppBar>
                    </BraxProviders>
                </UserContext.Provider>
            ) : (
                <BraxProviders>
                    { routers.rendered.unauthenticated }
                </BraxProviders>
            ) }
        </>);
    }

}));
